import * as React from 'react';
import { memo, useMemo, useCallback } from 'react';
import Image from 'next/image';
import { HomeIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'next-i18next';

import { cn, customImageLoader, getAssetSrc } from '@restworld/utils-common';

import { Card, PrimaryList, CardSubTitle, CardTitle } from '../../';
import { DefaultPrimaryArgsType } from './card.mocks';

export const CardPrimary = ({
  jobPositionDetails,
  showRestaurant,
  showViolaBadge,
  variant = 'white',
  twStyle,
}: DefaultPrimaryArgsType) => {
  const { t } = useTranslation('common');
  const formatSalaryContract = useCallback(
    ({ salary, contractTerm, shifts, salaryMonthNumber }: Partial<JobInfoType>) => [
      ...(salary
        ? [
            {
              key: 'salary',
              subTitle: `${salary}`,
              label: `${t('pay')}${salaryMonthNumber ? ` per ${salaryMonthNumber} ${t('monthly_payment')}` : ''}`,
              icon: 'CurrencyEuroIcon' as const,
            },
          ]
        : []),
      ...(contractTerm
        ? [
            {
              key: 'contract',
              subTitle: contractTerm,
              label: t('contract_title'),
              icon: 'DocumentTextIcon' as const,
            },
          ]
        : []),
      ...(shifts
        ? [
            {
              key: 'shifts',
              subTitle: shifts.subTitle,
              label: shifts.title,
              icon: 'ClockIcon' as const,
            },
          ]
        : []),
    ],
    [t]
  );

  const itemList = useMemo(
    () =>
      formatSalaryContract({
        ...(jobPositionDetails?.salary ? { salary: jobPositionDetails?.salary } : {}),
        ...(jobPositionDetails?.contractTerm ? { contractTerm: jobPositionDetails?.contractTerm } : {}),
        ...(jobPositionDetails?.shifts ? { shifts: jobPositionDetails?.shifts } : {}),
        salaryMonthNumber: jobPositionDetails?.salaryMonthNumber,
      }),
    [
      formatSalaryContract,
      jobPositionDetails?.contractTerm,
      jobPositionDetails?.salary,
      jobPositionDetails?.salaryMonthNumber,
      jobPositionDetails?.shifts,
    ]
  );
  return (
    <CardWrapper>
      <Card paddingType="large" variant={variant} twStyle={twStyle}>
        {showRestaurant && jobPositionDetails?.companyLogo ? (
          <ImageWrapper>
            <Image
              loader={customImageLoader}
              className="object-cover object-center w-full h-full rounded-lg"
              src={jobPositionDetails?.companyLogo}
              alt="logo"
              width={200}
              height={200}
            />
          </ImageWrapper>
        ) : null}
        <PrimaryCardHeader
          {...{
            showRestaurant,
            showViolaBadge,
            fontColor: variant === 'white' ? 'black' : 'white',
            ...(jobPositionDetails
              ? {
                  companyLogo: jobPositionDetails?.companyLogo,
                  seasonalAccommodation: jobPositionDetails?.seasonalAccommodation,
                  companyName: jobPositionDetails?.companyName,
                  title: jobPositionDetails?.title,
                  jobLocation: jobPositionDetails?.jobLocation,
                  jobLocality: jobPositionDetails?.jobLocality,
                }
              : {}),
          }}
        />
        {/* RestaurantStatistics removed — component lives in worker-app/components/legacy */}
        <PrimaryList itemList={itemList} type={variant === 'gradientBase' ? 'landing-card' : 'default'} />
      </Card>
    </CardWrapper>
  );
};
export default CardPrimary;

export const PrimaryCardHeader = memo((props: PrimaryCardHeaderType) => {
  const {
    showRestaurant,
    companyLogo,
    seasonalAccommodation,
    companyName,
    title,
    jobLocation: inputJobLocation,
    jobLocality,
    fontColor = 'black',
    twStyle,
    showViolaBadge,
  } = props;

  // Harcoded hiding of some specific keywords
  const jobLocation = useMemo(
    () => (inputJobLocation ? inputJobLocation.replace('Coloreto, ', '') : inputJobLocation),
    [inputJobLocation]
  );

  const jobLocationEl = useMemo(
    () =>
      (showRestaurant && jobLocation) || jobLocality ? (
        <div className="max-w-[12rem]">
          <CardSubTitle textColor={fontColor === 'black' ? 'gray' : 'gray300'}>
            {showRestaurant && jobLocation ? jobLocation : jobLocality}
          </CardSubTitle>
        </div>
      ) : null,
    [fontColor, jobLocality, jobLocation, showRestaurant]
  );
  return (
    <Header showRestaurant={Boolean(showRestaurant && companyLogo)} twStyle={twStyle}>
      {seasonalAccommodation ? <SeasonalAccommodation /> : null}
      {showRestaurant ? (
        <CardSubTitle textColor={fontColor === 'black' ? 'gray' : 'gray300'}>{companyName}</CardSubTitle>
      ) : null}
      {title ? <CardTitle textColor={fontColor}>{title}</CardTitle> : null}
      {jobLocationEl}
      {showViolaBadge && <ViolaBadge />}
    </Header>
  );
});
// --> parent > child components
const CardWrapper = ({ className, ...props }: any) => <div className={cn('w-full md:px-0', className)} {...props} />;
const ImageWrapper = ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
  <div
    className={cn(
      'absolute w-20 h-20 -top-10 mx-auto bg-white border border-primary-900 rounded-lg flex justify-center items-center self-center [box-shadow: 8px 7px 34px 0px rgba(48, 21, 96, 0.12)]',
      className
    )}
    {...props}
  />
);
const Header = ({ showRestaurant, twStyle, className, ...props }: any) => (
  <div
    className={cn('text-center flex flex-col gap-1 items-center', showRestaurant ? 'mt-7' : '', twStyle, className)}
    {...props}
  />
);
const SeasonalAccommodation = () => (
  <div className="flex flex-row justify-center mb-4">
    <span className="flex flex-row items-center gap-2 px-2 py-1 bg-primary-50 rounded-lg">
      <HomeIcon className="w-4 h-4 text-primary-700" />
      <span className="text-primary-700">Alloggio Incluso</span>
      <span role="img" aria-label="starred-face">
        🤩
      </span>
    </span>
  </div>
);

const ViolaBadge = () => (
  <div className="flex flex-row justify-center my-2">
    <span
      className="flex flex-row items-center gap-2 p-3 bg-primary-50 rounded-lg w-full transition-all cursor-pointer hover:bg-primary-100 group"
      onClick={() => window.open('https://donnexstrada.org/punti-viola/', '_blank')}
    >
      <Image src={getAssetSrc('/utils-assets/logo-dxs.png')} width={80} height={30} alt="Donnexstrada Punti Viola" />
      <span className="text-primary-700 text-sm group-hover:text-primary-900">
        Lo staff è formato contro la violenza di genere
      </span>
    </span>
  </div>
);

// --> type
interface JobInfoType {
  salary: string;
  contractTerm: string;
  shifts: {
    title: string;
    subTitle: string;
  };
  salaryMonthNumber?: number;
}
interface PrimaryCardHeaderType extends CompanyReviewProps {
  twStyle?: string;
}
export type CompanyReviewProps = {
  showRestaurant: boolean;
  companyLogo?: string;
  seasonalAccommodation?: boolean;
  companyName?: string;
  title?: string;
  jobLocation?: string;
  jobLocality?: string;
  fontColor?: 'white' | 'black';
  showViolaBadge?: boolean;
};
export * from './card.mocks';
